/**
 * usePersonalData Hook
 * Main hook for CRUD operations on user personal data
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@xanectech/auth';
import { EnhancedDataCategoryService } from '../services/EnhancedDataCategoryService';
import { syncSharedContractSubmissionsForFieldChanges } from '../services/ContractSubmissionService';
import {
  validateFieldValues,
  encryptValue,
  hashValue,
  shouldEncryptField,
} from '../utils/personal-data';
import type {
  DataCategory,
  CategoryEntry,
  FieldDefinition,
  CategoryFieldDataInsert,
} from '@xanectech/types';
import type {
  EntryData,
  EntryWithData,
  FieldWithValue,
} from '@xanectech/types';
import { toast } from '@xanectech/ui/lib';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - usePersonalData');

/**
 * Query keys for personal data
 */
export const personalDataKeys = {
  all: ['personalData'] as const,
  categories: () => [...personalDataKeys.all, 'categories'] as const,
  userEntries: (userId: string, categoryId: string) =>
    [...personalDataKeys.all, 'entries', userId, categoryId] as const,
  entryWithData: (entryId: string) =>
    [...personalDataKeys.all, 'entryData', entryId] as const,
};

/**
 * Return type for usePersonalData hook
 */
export interface UsePersonalDataReturn {
  // Data
  categories: DataCategory[];
  entries: CategoryEntry[];
  fieldData: unknown[];
  loading: boolean;
  error: Error | null;

  // Actions
  createEntry: (categoryId: string, data: EntryData) => Promise<void>;
  updateEntry: (entryId: string, data: Partial<EntryData>) => Promise<void>;
  deleteEntry: (entryId: string) => Promise<void>;

  // Utility
  getEntriesByCategory: (categoryId: string) => CategoryEntry[];
  getCompletionPercentage: (categoryId: string) => number;
  refresh: () => Promise<void>;
}

/**
 * Main personal data management hook
 * Provides CRUD operations for user's personal data entries
 */
export function usePersonalData(categoryId?: string): UsePersonalDataReturn {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | null>(null);

  // Fetch all categories
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: personalDataKeys.categories(),
    queryFn: () => EnhancedDataCategoryService.getCategories(),
  });

  // Fetch user's entries for specific category OR all entries if no category specified
  const {
    data: entries = [],
    isLoading: entriesLoading,
    refetch: refetchEntries,
  } = useQuery({
    queryKey: categoryId
      ? personalDataKeys.userEntries(String(user?.id) || '', categoryId)
      : [...personalDataKeys.all, 'allEntries', String(user?.id) || ''],
    queryFn: () =>
      categoryId
        ? EnhancedDataCategoryService.getUserCategoryEntries(
            String(user?.id) || '',
            categoryId,
          )
        : EnhancedDataCategoryService.getAllUserEntries(String(user?.id) || ''),
    enabled: !!user?.id,
  });

  // Fetch user's field data for search functionality (only when no categoryId)
  const {
    data: fieldData = [],
    isLoading: fieldDataLoading,
    refetch: refetchFieldData,
  } = useQuery({
    queryKey: [...personalDataKeys.all, 'fieldData', String(user?.id) || ''],
    queryFn: () =>
      EnhancedDataCategoryService.getAllUserFieldData(String(user?.id) || ''),
    enabled: !!user?.id && !categoryId, // Only fetch when getting all data
  });

  const loading = categoriesLoading || entriesLoading || fieldDataLoading;

  /**
   * Create a new entry with field data
   */
  const createEntryMutation = useMutation({
    mutationFn: async ({
      categoryId,
      data,
    }: {
      categoryId: string;
      data: EntryData;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Get field definitions for validation
      const fields =
        await EnhancedDataCategoryService.getCategoryFields(categoryId);

      // Validate field values
      const safeFieldValues: Record<string, string> =
        data.field_values && typeof data.field_values === 'object'
          ? Object.fromEntries(
              Object.entries(data.field_values as Record<string, string>)
                .filter(
                  ([k, v]) => typeof k === 'string' && typeof v === 'string',
                )
                .map(([k, v]) => [k, v as string]),
            )
          : {};

      const { is_valid, field_errors } = validateFieldValues(
        safeFieldValues,
        fields,
      );

      if (!is_valid) {
        const errorMessages = Object.values(field_errors).flat();
        throw new Error(errorMessages.join(', '));
      }

      // Create the entry
      const entry = await EnhancedDataCategoryService.createCategoryEntry({
        user_id: user.id,
        category_id: categoryId,
        entry_label: data.entry_label,
        label_manually_edited: data.label_manually_edited || false,
        entry_description: data.entry_description || null,
        is_primary: data.is_primary || false,
        is_favorite: data.is_favorite || false,
        tags: data.tags || null,
      });

      // Save field data
      if (data.field_values) {
        let fieldDataPromises: Promise<unknown>[] = [];
        if (
          data.field_values &&
          typeof data.field_values === 'object' &&
          !Array.isArray(data.field_values)
        ) {
          fieldDataPromises = Object.entries(
            data.field_values as Record<string, string>,
          ).map(async ([fieldId, value]) => {
            const field = fields.find((f) => f.id === fieldId);
            if (!field || !value) {
              if (!field) {
                logger.warn(
                  `[usePersonalData] Unknown field_id '${fieldId}' in field_values. Field definition not found. Skipping.`,
                );
              }
              return;
            }

            // Encrypt sensitive fields
            const shouldEncrypt = shouldEncryptField({
              is_sensitive: field.is_sensitive ?? false,
              is_pii: field.is_pii ?? false,
              data_type: field.data_type,
            });
            logger.info('[usePersonalData] About to encrypt:', {
              fieldId,
              shouldEncrypt,
              valueLength: value?.length,
            });
            const encryptedValue = shouldEncrypt
              ? await encryptValue(value)
              : value;
            const valueHash = shouldEncrypt ? await hashValue(value) : null;

            const fieldData: CategoryFieldDataInsert = {
              entry_id: entry.id,
              field_id: fieldId,
              value_encrypted: encryptedValue,
              value_hash: valueHash,
              is_verified: false,
              verified_at: null,
              verified_by: null,
            };

            return EnhancedDataCategoryService.createFieldData(fieldData);
          });
        }

        await Promise.all(fieldDataPromises);

        if (
          data.field_values &&
          typeof data.field_values === 'object' &&
          !Array.isArray(data.field_values)
        ) {
          const changedFieldValuesByFieldId = Object.fromEntries(
            Object.entries(data.field_values as Record<string, string>).filter(
              ([fieldId, value]) =>
                typeof fieldId === 'string' && typeof value === 'string',
            ),
          );

          await syncSharedContractSubmissionsForFieldChanges(
            changedFieldValuesByFieldId,
          );
        }
      }

      return entry;
    },
    onSuccess: () => {
      // Invalidate specific category entries
      void queryClient.invalidateQueries({
        queryKey: personalDataKeys.userEntries(
          String(user?.id) || '',
          categoryId || '',
        ),
      });
      // Also invalidate all personal data queries to ensure UI refresh
      void queryClient.invalidateQueries({
        queryKey: personalDataKeys.all,
      });
      toast({ description: 'Entry created successfully.', variant: 'success' });
    },
    onError: (err: Error) => {
      setError(err);
      toast({
        description: `Failed to create entry: ${err.message}`,
        variant: 'error',
      });
    },
  });

  /**
   * Update an existing entry
   */
  const updateEntryMutation = useMutation({
    mutationFn: async ({
      entryId,
      data,
    }: {
      entryId: string;
      data: Partial<EntryData>;
    }) => {
      // Update entry metadata
      const updates: Partial<EntryData> = {};
      if (data.entry_label) updates.entry_label = data.entry_label;
      if (data.entry_description !== undefined)
        updates.entry_description = data.entry_description;
      if (data.is_primary !== undefined) updates.is_primary = data.is_primary;
      if (data.is_favorite !== undefined)
        updates.is_favorite = data.is_favorite;
      if (data.tags !== undefined) updates.tags = data.tags;
      if (data.label_manually_edited !== undefined)
        updates.label_manually_edited = data.label_manually_edited;

      const entry = await EnhancedDataCategoryService.updateCategoryEntry(
        entryId,
        updates,
      );

      // Update field data if provided
      if (data.field_values) {
        const entryData =
          await EnhancedDataCategoryService.getEntryWithFieldData(entryId);
        const fields = await EnhancedDataCategoryService.getCategoryFields(
          String(entry.category_id),
        );

        const fieldDataPromises = Object.entries(
          data.field_values as Record<string, string>,
        ).map(async ([fieldId, value]) => {
          const field = fields.find((f) => f.id === fieldId);
          if (!field) {
            logger.warn(
              `[usePersonalData] Unknown field_id '${fieldId}' in field_values for entry_id '${entryId}' (category_id '${entry.category_id}'). Skipping.`,
            );
            return;
          }

          const existingFieldData = entryData.field_data.find(
            (fd) => fd.field_id === fieldId,
          );

          // Encrypt sensitive fields
          const shouldEncrypt = shouldEncryptField({
            is_sensitive: field.is_sensitive ?? false,
            is_pii: field.is_pii ?? false,
            data_type: field.data_type,
          });
          logger.info('[usePersonalData] About to encrypt (update):', {
            fieldId,
            shouldEncrypt,
            valueLength: value?.length,
          });
          const encryptedValue = shouldEncrypt
            ? await encryptValue(value)
            : value;
          const valueHash = shouldEncrypt ? await hashValue(value) : null;

          if (existingFieldData) {
            // Update existing field data
            return EnhancedDataCategoryService.updateFieldData(
              String(existingFieldData.id),
              {
                value_encrypted: encryptedValue,
                value_hash: valueHash,
              },
            );
          } else {
            // Create new field data
            const fieldData: CategoryFieldDataInsert = {
              entry_id: entryId,
              field_id: fieldId,
              value_encrypted: encryptedValue,
              value_hash: valueHash,
              is_verified: false,
              verified_at: null,
              verified_by: null,
            };
            return EnhancedDataCategoryService.createFieldData(fieldData);
          }
        });

        await Promise.all(fieldDataPromises);

        const changedFieldValuesByFieldId = Object.fromEntries(
          Object.entries(data.field_values as Record<string, string>).filter(
            ([fieldId, value]) =>
              typeof fieldId === 'string' && typeof value === 'string',
          ),
        );

        await syncSharedContractSubmissionsForFieldChanges(
          changedFieldValuesByFieldId,
        );
      }

      return entry;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: personalDataKeys.userEntries(
          String(user?.id) || '',
          categoryId || '',
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: personalDataKeys.all,
      });
      toast({ description: 'Entry updated successfully.', variant: 'success' });
    },
    onError: (err: Error) => {
      setError(err);
      toast({
        description: `Failed to update entry: ${err.message}`,
        variant: 'error',
      });
    },
  });

  /**
   * Delete an entry
   */
  const deleteEntryMutation = useMutation({
    mutationFn: async (entryId: string) => {
      await EnhancedDataCategoryService.deleteCategoryEntry(entryId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: personalDataKeys.userEntries(
          String(user?.id) || '',
          categoryId || '',
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: personalDataKeys.all,
      });
      toast({ description: 'Entry deleted successfully.', variant: 'success' });
    },
    onError: (err: Error) => {
      setError(err);
      toast({
        description: `Failed to delete entry: ${err.message}`,
        variant: 'error',
      });
    },
  });

  /**
   * Create entry wrapper
   */
  const createEntry = useCallback(
    async (categoryId: string, data: EntryData) => {
      await createEntryMutation.mutateAsync({ categoryId, data });
    },
    [createEntryMutation],
  );

  /**
   * Update entry wrapper
   */
  const updateEntry = useCallback(
    async (entryId: string, data: Partial<EntryData>) => {
      await updateEntryMutation.mutateAsync({ entryId, data });
    },
    [updateEntryMutation],
  );

  /**
   * Delete entry wrapper
   */
  const deleteEntry = useCallback(
    async (entryId: string) => {
      await deleteEntryMutation.mutateAsync(entryId);
    },
    [deleteEntryMutation],
  );

  /**
   * Get entries filtered by category
   */
  const getEntriesByCategory = useCallback(
    (categoryId: string): CategoryEntry[] => {
      return entries.filter((entry) => entry.category_id === categoryId);
    },
    [entries],
  );

  /**
   * Calculate completion percentage for a category
   */
  const getCompletionPercentage = useCallback(
    (categoryId: string): number => {
      const categoryEntries = entries.filter(
        (entry) => entry.category_id === categoryId,
      );

      if (categoryEntries.length === 0) return 0;

      // For now, return 0 - in a full implementation,
      // this would calculate based on filled fields
      // TODO: Implement full completion calculation
      return 0;
    },
    [entries],
  );

  /**
   * Refresh all data
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      refetchCategories(),
      refetchEntries(),
      refetchFieldData(),
    ]);
  }, [refetchCategories, refetchEntries, refetchFieldData]);

  return {
    categories,
    entries,
    fieldData,
    loading,
    error,
    createEntry,
    updateEntry,
    deleteEntry,
    getEntriesByCategory,
    getCompletionPercentage,
    refresh,
  };
}
